"use client";

import { useTranslations } from "next-intl";
import { useMemo } from "react";

import {
  ACTIVITY_TIME_LABEL_KEYS,
  AUDIT_VISUAL_EVENT_KEYS,
  SCRIPT_PROTECTION_MASKED_LINES,
  SCRIPT_PROTECTION_SHARE_MODE_IDS,
  SCRIPT_PROTECTION_VISIBLE_LINES,
  SECURITY_ACTIVITY_DEFINITIONS,
  SESSION_VISUAL_DEVICE_KEYS,
  SHARE_LINK_SETTING_KEYS,
  type ScriptProtectionShareModeId,
} from "@/content/security-mock-structure";
import type { ScreenplayElementData } from "@/features/security/components/visuals/ScreenplayElement";
import type {
  SecurityActivityEntry,
  SecurityHighlightTarget,
} from "@/features/security/components/security-hero/security-hero-visual.constants";

export type ShareLinkSetting = {
  key: (typeof SHARE_LINK_SETTING_KEYS)[number];
  label: string;
  value: string;
};

export type AuditVisualEvent = {
  key: (typeof AUDIT_VISUAL_EVENT_KEYS)[number];
  actor: string;
  action: string;
  time: string;
  highlight: boolean;
};

export type SessionVisualDevice = {
  key: (typeof SESSION_VISUAL_DEVICE_KEYS)[number];
  device: string;
  signedIn: string;
  current: boolean;
};

export type AuthMethodVisual = {
  id: "mfa" | "passkey" | "sso";
  title: string;
  detail: string;
  status: string;
  active: boolean;
};

export type ScriptProtectionShareModeVisual = {
  id: ScriptProtectionShareModeId;
  label: string;
  detail: string;
};

export type ScriptProtectionVisualContent = {
  draftTitle: string;
  externalShareView: string;
  productionLock: string;
  pageNumber: string;
  maskedPagesLabel: string;
  shareLinkHeader: string;
  watermarkEmail: string;
  expiresIn: string;
  formatExpiresDays: (days: number) => string;
  shareModes: ScriptProtectionShareModeVisual[];
  visibleScript: ScreenplayElementData[];
  maskedScript: ScreenplayElementData[];
};

const AUTH_METHOD_CONFIG = [
  { id: "mfa" as const, active: true },
  { id: "passkey" as const, active: true },
  { id: "sso" as const, active: false },
];

export const useSecurityActivityPool = (): SecurityActivityEntry[] => {
  const t = useTranslations("security.mock.activity");

  return useMemo(
    () =>
      SECURITY_ACTIVITY_DEFINITIONS.map((definition) => ({
        id: definition.id,
        user: t(`${definition.id}.user`),
        event: t(`${definition.id}.event`),
        highlightTarget: definition.highlightTarget,
      })),
    [t],
  );
};

export const useSecurityMockContent = () => {
  const t = useTranslations("security.mock");

  const shareLinkSettings = useMemo(
    (): ShareLinkSetting[] =>
      SHARE_LINK_SETTING_KEYS.map((key) => ({
        key,
        label: t(`shareLinkSettings.${key}.label`),
        value: t(`shareLinkSettings.${key}.value`),
      })),
    [t],
  );

  const activityTimeLabels = useMemo(
    () => ACTIVITY_TIME_LABEL_KEYS.map((key) => t(`timeLabels.${key}`)),
    [t],
  );

  const auditEvents = useMemo(
    (): AuditVisualEvent[] =>
      AUDIT_VISUAL_EVENT_KEYS.map((key) => ({
        key,
        actor: t(`auditEvents.${key}.actor`),
        action: t(`auditEvents.${key}.action`),
        time: t(`auditEvents.${key}.time`),
        highlight: key === "evt01",
      })),
    [t],
  );

  const sessionDevices = useMemo(
    (): SessionVisualDevice[] =>
      SESSION_VISUAL_DEVICE_KEYS.map((key, index) => ({
        key,
        device: t(`sessionDevices.${key}.device`),
        signedIn: t(`sessionDevices.${key}.signedIn`),
        current: index === 0,
      })),
    [t],
  );

  const authMethods = useMemo(
    (): AuthMethodVisual[] =>
      AUTH_METHOD_CONFIG.map(({ id, active }) => ({
        id,
        active,
        title: t(`authMethods.${id}.title`),
        detail: t(`authMethods.${id}.detail`),
        status: t(`authMethods.${id}.status`),
      })),
    [t],
  );

  const apiKeysVisual = useMemo(
    () => ({
      header: t("apiKeysVisual.header"),
      live: t("apiKeysVisual.live"),
      keyLabel: t("apiKeysVisual.keyLabel"),
      scopesLabel: t("apiKeysVisual.scopesLabel"),
    }),
    [t],
  );

  const scriptProtectionVisual = useMemo(
    (): ScriptProtectionVisualContent => ({
      draftTitle: t("scriptProtectionVisual.draftTitle"),
      externalShareView: t("scriptProtectionVisual.externalShareView"),
      productionLock: t("scriptProtectionVisual.productionLock"),
      pageNumber: t("scriptProtectionVisual.pageNumber"),
      maskedPagesLabel: t("scriptProtectionVisual.maskedPagesLabel"),
      shareLinkHeader: t("scriptProtectionVisual.shareLinkHeader"),
      watermarkEmail: t("scriptProtectionVisual.watermarkEmail"),
      expiresIn: t("scriptProtectionVisual.expiresIn"),
      formatExpiresDays: (days) =>
        t("scriptProtectionVisual.expiresDays", { days }),
      shareModes: SCRIPT_PROTECTION_SHARE_MODE_IDS.map((id) => ({
        id,
        label: t(`scriptProtectionVisual.shareModes.${id}.label`),
        detail: t(`scriptProtectionVisual.shareModes.${id}.detail`),
      })),
      visibleScript: SCRIPT_PROTECTION_VISIBLE_LINES.map(({ key, type }) => ({
        type,
        text: t(`scriptProtectionVisual.visibleScript.${key}`),
      })),
      maskedScript: SCRIPT_PROTECTION_MASKED_LINES.map(({ key, type }) => ({
        type,
        text: t(`scriptProtectionVisual.maskedScript.${key}`),
      })),
    }),
    [t],
  );

  return {
    workspaceActivity: t("workspaceActivity"),
    live: t("live"),
    shareLinkTitle: t("shareLinkTitle"),
    shareLinkSubtitle: t("shareLinkSubtitle"),
    scriptProtectionTitle: t("scriptProtectionTitle"),
    encryptionBadge: t("encryptionBadge"),
    sessionCurrentLabel: t("sessionCurrentLabel"),
    sessionSignOutOthersLabel: t("sessionSignOutOthersLabel"),
    sessionViewHistoryLabel: t("sessionViewHistoryLabel"),
    sessionRevokeLabel: t("sessionRevokeLabel"),
    browserUrl: t("browserUrl"),
    shareLinkSettings,
    activityTimeLabels,
    auditEvents,
    sessionDevices,
    authMethods,
    apiKeysVisual,
    scriptProtectionVisual,
  };
};

export type { SecurityHighlightTarget };
